import React from 'react';
import NiceModal from '@ebay/nice-modal-react';
import styled from 'styled-components';

import { TCustomerInsights } from 'containers/Intelligence/i18n/TModules';
import { tokens } from 'containers/Intelligence/theme';
import { intelligenceScrollbar } from 'containers/Intelligence/theme/scrollbar';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { BreakdownModalShell } from '../BreakdownModal';

/**
 * How many rows a ranked or progress-bar list draws on the card before the rest move behind its
 * `View more`.
 *
 * Customer 360 already caps its blocks at three (`C360_INLINE_ROW_LIMIT`) because its frames end on
 * a link by design. This limit answers a different question: an AI-screen list is drawn from a
 * distribution whose segment count nobody sizes in advance, so a tenant whose payment rail mix
 * carries twenty rails made the card taller than the screen. Six is the cut the user asked for.
 * PLA-8844.
 */
export const VIEW_MORE_ROW_LIMIT = 6;

/**
 * Everything the overlay inherits from the block that opened it. A list component caps itself only
 * when it is handed this object: the popup is titled by the block's own heading and states the card
 * it came from, and neither is derivable from a row array, so a list with no `viewMore` draws every
 * row exactly as it did before rather than opening an overlay with an invented header.
 */
export interface IListViewMore {
  /** The block's own heading — the overlay names the list it was opened from. */
  title: string;
  /** The card the block sits on; the shell prints it as the `From {{card}}` context line. */
  cardLabel: string;
  /** Rows past this stay behind the button. Defaults to `VIEW_MORE_ROW_LIMIT`. */
  threshold?: number;
  /** The card's denominator disclosure, where it carries one. */
  countingRule?: string;
}

const thresholdOf = (viewMore?: IListViewMore): number => {
  const asked = viewMore?.threshold;

  return Number.isInteger(asked) && (asked as number) > 0 ? (asked as number) : VIEW_MORE_ROW_LIMIT;
};

/** How many rows the overlay would show that the card does not already draw. */
export const hiddenRowCount = (rows: readonly unknown[], viewMore?: IListViewMore): number =>
  viewMore ? Math.max(rows.length - thresholdOf(viewMore), 0) : 0;

/** The slice the card draws inline — the whole set whenever nothing is hidden. */
export const inlineRowsFor = <T,>(rows: readonly T[], viewMore?: IListViewMore): readonly T[] =>
  hiddenRowCount(rows, viewMore) > 0 ? rows.slice(0, thresholdOf(viewMore)) : rows;

export interface IListBreakdownProps {
  title: string;
  cardLabel: string;
  countingRule?: string;
  /**
   * The SAME list the card draws, rendered with every row. Passing the rendered block rather than
   * rows and columns is what keeps the overlay identical to the card by construction: there is one
   * renderer, called twice.
   */
  body: React.ReactNode;
}

/* A reopened list is as long as the payload, so the body scrolls rather than the dialog growing
   past the viewport. The scroll lives here rather than on `BreakdownModalShell`, which Layer 1's
   breakdowns and Customer 360's own overlay share. */
const OVERLAY_BODY_MAX_HEIGHT = '60vh';

const Body = styled.div`
  max-height: ${OVERLAY_BODY_MAX_HEIGHT};
  overflow-x: auto;
  overflow-y: auto;

  /* The overlay body has no padding of its own, so it takes the gutter padding: this is the list
     the user reported drawn flush against the bar. */
  ${intelligenceScrollbar('both', { pad: true })}
`;

/**
 * The AI-screen overlay seam. Every capped list's `View more` opens THIS, over the same
 * `BreakdownModalShell` that Layer 1's breakdowns and Customer 360's `View More` already use, so the
 * three share one dialog rather than growing a second popup pattern.
 *
 * Like Customer 360's overlay it deliberately does NOT call `useBreakdownData`: the rows come from
 * the set the card already holds, handed over already rendered by the caller.
 */
export const ListBreakdownShell: React.FC<IListBreakdownProps> = ({
  title,
  cardLabel,
  countingRule,
  body
}) => (
  <BreakdownModalShell title={title} context={{ cardLabel }} countingRule={countingRule}>
    <Body data-testid="list-breakdown">{body}</Body>
  </BreakdownModalShell>
);

const ListBreakdown = NiceModal.create(ListBreakdownShell);

export const showListBreakdown = (props: IListBreakdownProps): void => {
  NiceModal.show(ListBreakdown, props);
};

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: ${tokens.space['4']};
`;

/* The ink, weight and focus ring Customer 360's four `View More` links already ship
   (`FinancialHealthSection`, `CustomerExperienceSection`): the AI screens take the same control
   rather than a second treatment of the same affordance. */
const ViewMoreButton = styled.button`
  padding: 0;
  border: 0;
  background: none;
  color: ${tokens.color['text-primary']};
  cursor: pointer;
  font-size: ${tokens.type['label-size']};
  font-weight: ${tokens.type['label-weight']};
  line-height: ${tokens.type['label-line']};

  &:focus-visible {
    border-radius: ${tokens.radius.xs};
    box-shadow: ${tokens.elevation['focus-ring']};
    outline: none;
  }
`;

export interface IListViewMoreFooterProps {
  /** The FULL row set, not the slice the card drew. */
  rows: readonly unknown[];
  viewMore?: IListViewMore;
  /**
   * The same list rendered with every row, built only once the reader asks for it — a card that is
   * hiding nothing never pays for a second render tree.
   */
  renderFull: () => React.ReactNode;
  className?: string;
}

/**
 * The block's trailing `View more`, or nothing at all. The answer is derived from the row set the
 * block was handed rather than decided per call site, so a short payload draws no control — the
 * button is not merely inert — and the same block draws one the moment a live payload carries a
 * seventh row.
 *
 * It reads the tenant's language through `useMultiLanguageTranslation`, which reaches the redux
 * store, so the list components mount it only once `hiddenRowCount` is positive: rendered
 * unconditionally it would hand every consumer of a plain ranking a store dependency it never had.
 */
export const ListViewMoreFooter: React.FC<IListViewMoreFooterProps> = ({
  rows,
  viewMore,
  renderFull,
  className
}) => {
  const { t } = useMultiLanguageTranslation();
  const hidden = hiddenRowCount(rows, viewMore);

  if (!viewMore || hidden === 0) {
    return null;
  }

  return (
    <Footer className={className} data-testid="list-view-more">
      <ViewMoreButton
        type="button"
        aria-haspopup="dialog"
        data-testid="list-view-more-button"
        onClick={() =>
          showListBreakdown({
            title: viewMore.title,
            cardLabel: viewMore.cardLabel,
            countingRule: viewMore.countingRule,
            body: renderFull()
          })
        }
      >
        {t(`${TCustomerInsights}.Breakdowns.ViewMore`, { hidden })}
      </ViewMoreButton>
    </Footer>
  );
};

export default ListBreakdown;
